/**
 * Hook that derives a grouped prop-type menu from the available props list.
 * Categorizes prop types into labelled sections for the filter dropdown.
 */

import type { BettableProp } from '@utils/propUtils';
import { formatPropType } from '@utils/propUtils';
import { useMemo } from 'react';

interface PropTypeItem {
  key: string;
  label: string;
}

export interface PropTypeSection {
  label: string;
  items: PropTypeItem[];
}

// ---------------------------------------------------------------------------
// Static category definitions
// ---------------------------------------------------------------------------
// API returns types like 'points', 'rebounds' (not 'player_points'), so both
// short and prefixed forms are listed here.
const PROP_SECTIONS: { label: string; keys: string[] }[] = [
  {
    label: 'Game',
    keys: [
      'h2h',
      'spreads',
      'totals',
      'spread',
      'total',
      'moneyline',
      'alternate_spreads',
      'alternate_totals',
    ],
  },
  { label: 'Team', keys: ['team_totals', 'team_total', 'alternate_team_totals'] },
  {
    label: 'Player',
    keys: [
      'points',
      'rebounds',
      'assists',
      'threes',
      'blocks',
      'steals',
      'turnovers',
      'player_points',
      'player_rebounds',
      'player_assists',
      'player_threes',
      'player_blocks',
      'player_steals',
      'player_turnovers',
    ],
  },
  {
    label: 'Shooting',
    keys: ['field_goals', 'frees_made', 'player_field_goals', 'player_frees_made'],
  },
  {
    label: 'Combo',
    keys: [
      'blocks_steals',
      'points_rebounds_assists',
      'points_rebounds',
      'points_assists',
      'rebounds_assists',
      'player_blocks_steals',
      'player_points_rebounds_assists',
      'player_points_rebounds',
      'player_points_assists',
      'player_rebounds_assists',
    ],
  },
  {
    label: 'Special',
    keys: [
      'double_double',
      'triple_double',
      'first_basket',
      'first_team_basket',
      'player_double_double',
      'player_triple_double',
      'player_first_basket',
      'player_first_team_basket',
    ],
  },
  {
    label: 'Half',
    keys: [
      'h2h_h1',
      'h2h_h2',
      'spreads_h1',
      'spreads_h2',
      'totals_h1',
      'totals_h2',
      'team_totals_h1',
      'team_totals_h2',
      'alternate_spreads_h1',
      'alternate_spreads_h2',
      'alternate_totals_h1',
      'alternate_totals_h2',
      'alternate_team_totals_h1',
      'alternate_team_totals_h2',
    ],
  },
  {
    label: 'Quarter',
    keys: [
      'h2h_q1',
      'h2h_q2',
      'h2h_q3',
      'h2h_q4',
      'spreads_q1',
      'spreads_q2',
      'spreads_q3',
      'spreads_q4',
      'totals_q1',
      'totals_q2',
      'totals_q3',
      'totals_q4',
      'team_totals_q1',
      'team_totals_q2',
      'team_totals_q3',
      'team_totals_q4',
      'points_q1',
      'rebounds_q1',
      'assists_q1',
      'player_points_q1',
      'player_rebounds_q1',
      'player_assists_q1',
    ],
  },
];

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Returns prop types grouped into display sections derived from the list of
 * available props. Sections with no matching props are omitted.
 */
export function usePropTypesBySection(availableProps: BettableProp[]): PropTypeSection[] {
  return useMemo(() => {
    const types = new Set<string>();
    availableProps.forEach((prop) => {
      if (prop.propType) {
        const baseType = prop.propType.replace(/_alternate$/, '');
        types.add(baseType);
      }
    });

    const categorized = new Set<string>();
    const sections: PropTypeSection[] = [];

    for (const section of PROP_SECTIONS) {
      const items: PropTypeItem[] = [];
      for (const key of section.keys) {
        if (types.has(key)) {
          items.push({ key, label: formatPropType(key) });
          categorized.add(key);
        }
      }
      if (items.length > 0) {
        items.sort((a, b) => a.label.localeCompare(b.label));
        sections.push({ label: section.label, items });
      }
    }

    // Catch any uncategorized types
    const uncategorized: PropTypeItem[] = [];
    for (const key of types) {
      if (!categorized.has(key)) {
        uncategorized.push({ key, label: formatPropType(key) });
      }
    }
    if (uncategorized.length > 0) {
      uncategorized.sort((a, b) => a.label.localeCompare(b.label));
      sections.push({ label: 'Other', items: uncategorized });
    }

    return sections;
  }, [availableProps]);
}
